import React from 'react';
import { css, Global } from '@emotion/core';
import { Helmet } from 'react-helmet';
import styled from '@emotion/styled';
import cssReset from '@atlaskit/css-reset';
import { layers } from '@atlaskit/theme/constants';
import { PropsTable } from 'pretty-proptypes';
import { N10, N30 } from '@atlaskit/theme/src/colors';
import { MDXProvider } from '@mdx-js/react';
import MetaTags from './meta-tags';
import HeaderContent from './../content/header-content';
import FooterContent from './../content/footer-content';
import Typography from './typography';
import Example from './example';
import PropsLogger from '../utilities/props-logger';

type Props = {
  sidebar?: any;
  title?: string;
  description?: string;
  children: any;
  footer?: any;
};

const Grid = styled.div`
  display: grid;
  min-height: 100vh;
  position: relative;
  ${(props: Props) =>
    props.sidebar
      ? `
    grid-template-columns: 272px 1fr;
    grid-template-areas:
      'header header'
      'sidebar main'
      'sidebar footer';
  `
      : `
    grid-template-areas:
        'header header'
        'main main'
        'footer footer';
    footer {
      text-align: center;
    }
  `}
  grid-template-rows: auto 1fr auto;
`;

/*
    - 404, restrict pages have no content and need to show empty state in the middle of main
    - components and other pages with content should have the main content aligned left
*/
const Main = styled.main`
  grid-area: main;
  display: grid;
  position: relative;
  grid-template-rows: auto 1fr;
  grid-template-columns: minmax(640px, 960px) 1fr;
  grid-template-areas:
    'hero hero'
    'content nav';
`;

const contentGrid = css`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
  grid-auto-rows: max-content;
`;

export const MainContent = styled.div`
  ${contentGrid};
  grid-area: content;
  padding-left: 80px;
  padding-bottom: 80px;
  padding-top: 32px;
  padding-right: 80px;
  margin-top: 0;
  max-width: 960px;
  /* We absolutely position child tabs. */
  position: relative;

  > * {
    grid-column: 1 / span 12;
    margin-top: 0;
    margin-bottom: 0;
  }

  > h2 {
    margin-top: 32px;
  }

  > h3 {
    margin-top: 16px;
  }

  table {
    thead {
      tr {
        /* Hack to work around pretty prop types heading not being bold atm. */
        font-weight: 500;
      }
    }

    td > div {
      /* Hack to remove margin from ERT div. */
      margin: 0 !important;
    }
    td:not(:last-child) {
      padding-right: 8px;
    }
  }
`;

export const CardGroup: React.FC = props => (
  <div css={contentGrid} {...props} />
);

const Header = styled.div`
  grid-area: header;
  z-index: ${layers.navigation()};
  position: sticky;
  top: 0;
`;

const Sidebar = styled.div`
  grid-area: sidebar;
  border-right: 1px solid ${N30};
`;

const Footer = styled.footer`
  grid-area: footer;
  background-color: ${N10};
`;

export const SpanAllContent = styled.div`
  align-self: center;
  grid-area: content / content / nav / nav;
`;

const Layout = (props: Props) => {
  let SidebarComponent = props.sidebar;
  let FooterComponent = props.footer || FooterContent;

  const shortcodes = {
    Example,
    PropsLogger,
    PropsTable,
  };

  return (
    <>
      <MDXProvider components={shortcodes}>
        <Global
          styles={css`
            ${cssReset}
            * {
              box-sizing: inherit;
            }
            body,
            html {
              box-sizing: border-box;
            }
          `}
        />
        <Typography />
        <MetaTags title={props.title} description={props.description} />

        {process.env.GATSBY_CONSTELLATION_ENVIRONMENT !== 'PROD' ? (
          <Helmet>
            <meta name="robots" content="noindex" />
          </Helmet>
        ) : null}

        <Grid sidebar={props.sidebar}>
          <Header>
            <HeaderContent />
          </Header>

          {SidebarComponent ? (
            <Sidebar>
              <SidebarComponent />
            </Sidebar>
          ) : null}

          <Main>{props.children}</Main>

          <Footer>
            <FooterComponent />
          </Footer>
        </Grid>
      </MDXProvider>
    </>
  );
};

export default Layout;
